import { useCallback, useEffect, useRef } from 'react';

/**
 * 简单的节流 Hook 实现
 * @param fn 需要节流的函数
 * @param delay 节流延迟时间（毫秒）
 * @returns 节流后的函数
 */
export const useThrottle = <T extends (...args: any[]) => any>(
    fn: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastRunRef = useRef<number>(0);
    const lastArgsRef = useRef<Parameters<T> | null>(null);
    
    const throttledFn = useCallback((...args: Parameters<T>) => {
        const now = Date.now();
        const timeSinceLastRun = now - lastRunRef.current;
        lastArgsRef.current = args;
        
        if (timeSinceLastRun >= delay) {
            fn(...args);
            lastRunRef.current = now;
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                if (lastArgsRef.current) {
                    fn(...lastArgsRef.current);
                    lastRunRef.current = Date.now();
                }
            }, delay - timeSinceLastRun);
        }
    }, [fn, delay]);
    
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    
    return throttledFn;
};

/**
 * 创建一个带有 run 方法的节流对象，兼容 @tinper/hooks 的 useThrottleFn API
 * @param fn 需要节流的函数
 * @param options 节流配置
 * @returns 包含 run 方法的对象
 */
export const useThrottleFn = <T extends (...args: any[]) => any>(
    fn: T,
    options: { wait: number }
): { run: (...args: Parameters<T>) => void } => {
    const throttledFn = useThrottle(fn, options.wait);
    return { run: throttledFn };
};